import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { useFavorites } from '../hooks/useFavorites'
import ProfileSection from './auth/ProfileSection'
import ActivityStatsSection from './auth/ActivityStatsSection'
import UserActivitySection from './auth/UserActivitySection'
import AuthForm from './auth/AuthForm'

/**
 * ImmersiveAuthPanel - Authentication and profile management panel
 * 
 * Features:
 * - Login/signup forms when not authenticated
 * - User profile and activity when authenticated
 * - Modular architecture with sub-components
 * 
 * @idempotent - Pure component, delegates to specialized children
 * @atomic - Now <90 lines after splitting into modular components (was 1336 lines)
 */
const ImmersiveAuthPanel = memo(function ImmersiveAuthPanel({
  user,
  stats,
  statsLoading,
  pendingConfirmations,
  loadingConfirmations,
  confirmingId,
  handleConfirmSighting,
  userLocation,
  entityCount,
  loginFormData,
  loginErrors,
  handleLoginChange,
  handleLoginSubmit,
  registerFormData,
  registerErrors,
  handleRegisterChange,
  handleRegisterSubmit,
  isAuthLoading,
  googleAuth,
  onTabChange
}) {
  const { t, i18n } = useTranslation()
  
  // Favorites hook for favoriteCount
  const { favoriteCount } = useFavorites(user, userLocation)

  // Show account portal when logged in
  if (user) {
    return (
      <div className="space-y-3 px-1">
        {/* Zone 1: Identity */}
        <ProfileSection user={user} i18n={i18n} t={t} />

        {/* Zone 2: Compact stats + badges */}
        <ActivityStatsSection 
          stats={stats} 
          statsLoading={statsLoading} 
          favoriteCount={favoriteCount}
          entityCount={entityCount}
        />

        {/* Zone 3: My Activity (personal only — sightings, favorites, bookings, requests) */}
        <UserActivitySection
          user={user}
          userLocation={userLocation}
          onTabChange={onTabChange}
        />
      </div>
    )
  }

  // Show auth forms when not logged in
  return (
    <div className="space-y-2">
      <AuthForm
        onTabChange={onTabChange}
        loginFormData={loginFormData}
        loginErrors={loginErrors}
        handleLoginChange={handleLoginChange}
        handleLoginSubmit={handleLoginSubmit}
        registerFormData={registerFormData}
        registerErrors={registerErrors}
        handleRegisterChange={handleRegisterChange}
        handleRegisterSubmit={handleRegisterSubmit}
        isAuthLoading={isAuthLoading}
        googleAuth={googleAuth}
      />
    </div>
  )
})

export default ImmersiveAuthPanel
